import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';

export interface HeroMetric {
  value: string;
  label: string;
  size?: 'small' | 'medium' | 'large';
  align?: 'left' | 'center' | 'right';
  shrinkText?: boolean;
}

export interface SubCarouselItem {
  value: string;
  label: string;
  group?: string; // Optional day grouping
}

export interface HeroProps {
  title: string;
  subtitle: string;
  subtitle2?: string;
  metrics?: HeroMetric[];
  subCarousel?: SubCarouselItem[];
  progressValue?: number;
  progressLabel?: string;
  gradient?: string;
  minHeight?: number;
  onClick?: () => void;
}

export const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  subtitle2,
  metrics,
  subCarousel,
  progressValue,
  progressLabel = "Progress",
  gradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  minHeight = 320,
  onClick
}) => {
  // State for cycling through sub-carousel items
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);

  // Group items by day in workout order
  const groupedItems = React.useMemo(() => {
    if (!subCarousel || subCarousel.length === 0) return [];
    
    const groups: { [day: string]: SubCarouselItem[] } = {};
    
    subCarousel.forEach(item => {
      const day = item.group || 'General';
      if (!groups[day]) {
        groups[day] = [];
      }
      groups[day].push(item);
    });
    
    // Define workout day order
    const workoutDayOrder = ['Monday', 'Tuesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Sort groups by workout day order
    const sortedGroups = workoutDayOrder
      .filter(day => groups[day] && groups[day].length > 0)
      .map(day => ({
        day,
        items: groups[day]
      }));
    
    // Add any remaining days not in the standard order (like 'General')
    Object.entries(groups).forEach(([day, items]) => {
      if (!workoutDayOrder.includes(day)) {
        sortedGroups.push({ day, items });
      }
    });
    
    return sortedGroups;
  }, [subCarousel]);

  // Reset indices when data changes
  useEffect(() => {
    setCurrentDayIndex(0);
    setCurrentExerciseIndex(0);
  }, [groupedItems]);

  // Auto-cycle through days and exercises
  useEffect(() => {
    if (!groupedItems || groupedItems.length === 0) return;

    const interval = setInterval(() => {
      const currentDayItems = groupedItems[currentDayIndex]?.items || [];
      const nextExerciseIndex = currentExerciseIndex + 1;
      
      // If we've cycled through all exercises in current day, move to next day
      if (nextExerciseIndex >= currentDayItems.length) {
        setCurrentDayIndex((prevDayIndex) => 
          (prevDayIndex + 1) % groupedItems.length
        );
        setCurrentExerciseIndex(0); // Reset to first exercise of next day
      } else {
        setCurrentExerciseIndex(nextExerciseIndex);
      }
    }, 2000); // Change every 2 seconds

    return () => clearInterval(interval);
  }, [groupedItems, currentDayIndex, currentExerciseIndex]);

  // Helper function to get font size based on metric size and shrinkText
  const getFontSize = (metric: HeroMetric, isValue: boolean = true) => {
    const baseSize = metric.size || 'medium';
    const shouldShrink = metric.shrinkText || false;
    
    if (isValue) {
      // Value font sizes - more aggressive shrinking
      switch (baseSize) {
        case 'small':
          return shouldShrink 
            ? { xs: '0.8rem', sm: '1rem' }
            : { xs: '1.2rem', sm: '1.4rem' };
        case 'large':
          return shouldShrink 
            ? { xs: '1.2rem', sm: '1.4rem' }
            : { xs: '1.8rem', sm: '2.2rem' };
        case 'medium':
        default:
          return shouldShrink 
            ? { xs: '1rem', sm: '1.2rem' }
            : { xs: '1.8rem', sm: '2.2rem' };
      }
    } else {
      // Label font sizes - keep labels readable, only slight shrinking
      return shouldShrink 
        ? { xs: '0.85rem', sm: '0.95rem' }
        : { xs: '0.9rem', sm: '1rem' };
    }
  };

  // Helper function to get text alignment based on grid position
  const getGridAlignment = (index: number) => {
    // Items 0 and 2 (1st and 3rd) are left-aligned
    // Items 1 and 3 (2nd and 4th) are right-aligned
    return (index === 0 || index === 2) ? 'left' : 'right';
  };

  // Render sub-carousel if provided
  const renderSubCarousel = () => {
    if (!groupedItems || groupedItems.length === 0) return null;

    const currentDayGroup = groupedItems[currentDayIndex];
    if (!currentDayGroup) return null;

    const currentItem = currentDayGroup.items[currentExerciseIndex];
    if (!currentItem) return null;

    return (
      <Box sx={{ 
        my: 3, 
        textAlign: 'center',
        transition: 'opacity 0.3s ease-in-out',
        minHeight: '180px', // Reduced for tighter spacing
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 1.5 // Consistent spacing between elements
      }}>
        {/* Day title and indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography 
            variant="h5"
            component="div" 
            fontWeight="bold"
            sx={{ 
              fontSize: { xs: '1.2rem', sm: '1.5rem' },
              lineHeight: 1.2,
              opacity: 0.8
            }}
          >
            {currentDayGroup.day}
          </Typography>
          {groupedItems.length > 1 && (
            <Typography 
              variant="caption"
              sx={{ 
                opacity: 0.6,
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                px: 1,
                py: 0.25,
                borderRadius: 1
              }}
            >
              {currentDayIndex + 1}/{groupedItems.length}
            </Typography>
          )}
        </Box>
        
        {/* Main value */}
        <Typography 
          variant="h2"
          component="div" 
          fontWeight="bold"
          sx={{ 
            fontSize: { xs: '2.2rem', sm: '2.7rem' },
            lineHeight: 1.2
          }}
        >
          {currentItem.value}
        </Typography>
        
        {/* Exercise name - full width, allow 2 lines */}
        <Box sx={{ 
          width: '100%', 
          px: { xs: .5, sm: 1 }, // Responsive padding
          minHeight: '3.2em', // Reserve space for 2 lines
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              opacity: 0.9,
              fontSize: { xs: '1rem', sm: '1.2rem' },
              lineHeight: 1.6,
              fontWeight: 500,
              textAlign: 'center',
              width: '100%',
              // Remove webkit box constraints that were preventing wrapping
              wordBreak: 'break-word',
              whiteSpace: 'normal'
            }}
          >
            {currentItem.label}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Determine layout based on number of metrics
  const renderMetrics = () => {
    if (!metrics || metrics.length === 0) return null;

    if (metrics.length === 1) {
      // Single metric - left align
      const metric = metrics[0];
      return (
        <Box sx={{ textAlign: 'left', my: 3 }}>
          <Typography 
            variant="h2"
            component="div" 
            fontWeight="bold"
            sx={{ fontSize: getFontSize(metric, true) }}
          >
            {metric.value}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            {metric.label}
          </Typography>
        </Box>
      );
    } else if (metrics.length === 2) {
      // Two metrics - first left, second right
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          my: 3,
          gap: 2
        }}>
          {metrics.map((metric, index) => (
            <Box 
              key={index}
              sx={{ 
                textAlign: index === 0 ? 'left' : 'right',
                flex: 1
              }}
            >
              <Typography 
                variant="h3"
                component="div" 
                fontWeight="bold"
                sx={{ 
                  fontSize: getFontSize(metric, true),
                  lineHeight: 1.2,
                  mb: 1
                }}
              >
                {metric.value}
              </Typography>
              <Typography variant="body1" sx={{ 
                opacity: 0.9,
                fontSize: getFontSize(metric, false)
              }}>
                {metric.label}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    } else if (metrics.length === 3) {
      // Three metrics - use 2x2 grid with proper alignment
      return (
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 1.5,
          my: 2,
        }}>
          {metrics.map((metric, index) => (
            <Box key={index} sx={{ 
              textAlign: getGridAlignment(index),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              py: 1,
              // Ensure proper grid positioning
              gridColumn: index === 0 ? '1' : index === 1 ? '2' : '1',
              gridRow: index === 0 ? '1' : index === 1 ? '1' : '2'
            }}>
              <Typography 
                variant="h3"
                component="div" 
                fontWeight="bold"
                sx={{ 
                  fontSize: getFontSize(metric, true),
                  lineHeight: 1.2,
                  mb: 1
                }}
              >
                {metric.value}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  opacity: 0.9,
                  fontSize: getFontSize(metric, false),
                  lineHeight: 1.4,
                  textAlign: getGridAlignment(index)
                }}
              >
                {metric.label}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    } else if (metrics.length === 4) {
      // Exactly four metrics - use 2x2 grid with proper alignment
      return (
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 1.5,
          my: 2,
        }}>
          {metrics.map((metric, index) => (
            <Box key={index} sx={{ 
              textAlign: getGridAlignment(index),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              py: 1
            }}>
              <Typography 
                variant="h3"
                component="div" 
                fontWeight="bold"
                sx={{ 
                  fontSize: getFontSize(metric, true),
                  lineHeight: 1.2,
                  mb: 1
                }}
              >
                {metric.value}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  opacity: 0.9,
                  fontSize: getFontSize(metric, false),
                  lineHeight: 1.4,
                  textAlign: getGridAlignment(index)
                }}
              >
                {metric.label}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    } else {
      // More than four metrics - use smaller 2x2 grid with consistent alignment
      return (
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 2,
          my: 3
        }}>
          {metrics.map((metric, index) => (
            <Box key={index} sx={{ 
              textAlign: getGridAlignment(index),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              py: 1
            }}>
              <Typography 
                variant="h5"
                component="div" 
                fontWeight="bold"
                sx={{ 
                  fontSize: getFontSize(metric, true),
                  lineHeight: 1.2,
                  mb: 1
                }}
              >
                {metric.value}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.9,
                  fontSize: getFontSize(metric, false),
                  lineHeight: 1.4,
                  textAlign: getGridAlignment(index)
                }}
              >
                {metric.label}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
  };

  return (
    <Card 
      onClick={onClick}
      sx={{ 
        background: gradient,
        color: 'white',
        mb: 3,
        position: 'relative',
        overflow: 'hidden !important',
        height: minHeight,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 6,
        } : {},
        '&::before, &::after': {
          overflow: 'hidden !important',
        },
      }}
      className="hero-card"
    >
      {/* Clipping container for decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          borderRadius: 'inherit',
          pointerEvents: 'none',
        }}
      >
        {/* Background decoration circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
          width: 120,
          height: 120,
          borderRadius: '50%',
          bgcolor: 'rgba(255, 255, 255, 0.1)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
            bottom: -20,
            left: -20,
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: 'rgba(255, 255, 255, 0.05)',
        }}
      />
      </Box>
      
      <CardContent sx={{ 
        p: 4, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'space-between' 
      }}>
        {/* Header Section */}
        <Box>
          <Typography 
            variant="h4" 
            component="h1" 
            fontWeight="bold" 
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              opacity: 0.9,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            {subtitle}
          </Typography>
          {subtitle2 && (
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.8, 
                mt: 0.5,
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              {subtitle2}
            </Typography>
          )}
        </Box>
        
        {/* Metrics or Sub-carousel Section */}
        {subCarousel && subCarousel.length > 0 ? renderSubCarousel() : renderMetrics()}
        
        {/* Progress Section */}
        {progressValue !== undefined && (
        <Box>
          <LinearProgress 
            variant="determinate" 
            value={progressValue} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'white',
              }
            }} 
          />
          {progressLabel && (
            <Typography 
              variant="caption" 
              sx={{ 
                opacity: 0.8, 
                mt: 1, 
                display: 'block',
                textAlign: 'center'
              }}
            >
              {progressLabel}
            </Typography>
          )}
        </Box>
      )}
      </CardContent>
    </Card>
  );
}; 